import { useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { useInfiniteQuery } from '@tanstack/react-query'

type useInfinitePaginationProps<T, K> = {
  items: T[]
  limit?: number
  params: K extends Record<string, unknown> ? K : never
  key: string
  getNextPageCallback: (pageParam: number, params: K) => Promise<T[]>
}

export const useInfinitePagination = <T, K>({
  items,
  getNextPageCallback,
  limit = 40,
  params,
  key
}: useInfinitePaginationProps<T, K>) => {
  const { ref, inView } = useInView({
    rootMargin: '10%',
    threshold: 0.1,
    triggerOnce: false
  })

  const initialItemsCountKey = items.length

  //react-query type errors, we need to include new function and ignore for 'queryKey'
  //https://github.com/TanStack/query/issues/1462
  const infiniteQuery = useInfiniteQuery({
    queryKey: [
      key,
      params.slug,
      JSON.stringify(params.query),
      limit,
      initialItemsCountKey,
      'productIds' in params ? JSON.stringify(params.productIds) : undefined,
      'distributionCenterId' in params ? params.distributionCenterId : undefined
    ],
    queryFn: ({ pageParam }) => {
      return getNextPageCallback(pageParam, {
        ...params,
        limit
      })
    },
    initialPageParam: 1,

    getNextPageParam: (lastPages, allPages) => {
      if (lastPages && lastPages.length < limit) {
        return undefined
      }

      return allPages.length + 1
    },
    initialData: {
      pages: [items],
      pageParams: [1]
    }
  })

  const flatItems = useMemo(
    () =>
      infiniteQuery.data?.pages.flatMap(page => {
        return page
      }),
    [infiniteQuery.data]
  )

  return {
    ref,
    items: flatItems,
    isFetching: infiniteQuery.isFetching,
    status: infiniteQuery.status,
    inView,
    fetchNextPage: infiniteQuery.fetchNextPage,
    refetch: infiniteQuery.refetch,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    hasNextPage: infiniteQuery.hasNextPage
  }
}
